import { CSSProperties } from "react";
import { useOrderInfoContext } from "@src/app/order-info-context";

interface Props {
  date: number;
  validTimeRange?: ValidTimeRange;
}

interface ValidTimeRange {
  earliestValidTime: number;
  latterValidTime: number;
}

const getDateUTC = (year: number, month: number, day: number) => {
  return new Date(Date.UTC(year, month, day));
};

const getDateUTCText = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

const isDateBeforeToday = (date: Date): boolean => {
  return new Date(date.toDateString()) < new Date(new Date().toDateString());
};

const isDateOutOfRange = (date: Date, range: ValidTimeRange | undefined) => {
  if (!range) return false;
  return date.getTime() < range.earliestValidTime || date.getTime() > range.latterValidTime;
};

const Month = ({ date, validTimeRange }: Props) => {
  const { firstCollection, schedule, setSchedule, setFirstCollection } = useOrderInfoContext();
  if (!schedule) setSchedule([0, 1, 2, 3, 4, 5, 6]);

  const dateObj = new Date(date);
  const monthStr = dateObj.toLocaleString("default", { month: "long" });
  const lastDayOfMonth = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0);

  const now = new Date();
  const currentDateUTC = getDateUTC(now.getFullYear(), now.getMonth(), now.getDate());
  const currentDateText = getDateUTCText(currentDateUTC);

  const handleSelectDate = (cellDate: Date, checked: boolean) => {
    if (!checked) {
      return;
    }

    if (setFirstCollection) {
      setFirstCollection(cellDate.toISOString().split("T")[0]);
    }
  };

  const renderDays = () => {
    let column = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1).getDay();
    if (column == 0) {
      column = 7;
    }
    let row = 2;

    const output = [];
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      if (column > 7) {
        column = 1;
        row++;
      }
      const cellDate = getDateUTC(dateObj.getFullYear(), dateObj.getMonth(), i);
      const style: CSSProperties = {
        gridColumnStart: column,
        gridColumnEnd: column,
        gridRowStart: row,
        gridRowEnd: row,
      };

      const value = getDateUTCText(cellDate);
      let enabled = false;

      if (!isDateBeforeToday(cellDate) && !isDateOutOfRange(cellDate, validTimeRange)) {
        enabled = schedule?.includes(cellDate.getDay() > 0 && !isDateBeforeToday(cellDate) ? cellDate.getDay() - 1 : 6) ?? false;
      }
      const checked = firstCollection === value;

      const cellClasses = ["tfm", "fm-a-month-cell", "fm-body-sm-medium", "fm-a-month-cell--" + (enabled ? "enabled" : "disabled")];
      if (value === currentDateText) {
        cellClasses.push("fm-a-month-cell--current");
      }

      checked && cellClasses.push("fm-a-month-cell--selected");

      output.push(
        <button
          key={i}
          className={cellClasses.join(" ")}
          tabIndex={enabled ? undefined : -1}
          style={style}
          onClick={() => handleSelectDate(cellDate, enabled)}
        >
          {i}
        </button>
      );
      column++;
    }
    return output;
  };

  return (
    <div className="fm-m-month-grid">
      <h4 className="fm-m-month-grid__title">{monthStr}</h4>
      <div className="fm-m-month-grid__grid" translate="no">
        <div className="fm-m-month-grid__day-name">MO</div>
        <div className="fm-m-month-grid__day-name">TU</div>
        <div className="fm-m-month-grid__day-name">WE</div>
        <div className="fm-m-month-grid__day-name">TH</div>
        <div className="fm-m-month-grid__day-name">FR</div>
        <div className="fm-m-month-grid__day-name">SA</div>
        <div className="fm-m-month-grid__day-name">SU</div>
        {renderDays()}
      </div>
    </div>
  );
};

export default Month;
